import React from 'react';

import { ElementClickTracker } from '../../analytics/cohesion';
import {
  ELEVATE_PROFILE_TRACKING,
  GET_STARTED_TRACKING,
  MAX_PROVIDERS_LIMIT,
  SIGN_UP_ELEVATE_PRESENCE_TRACKING,
  SIGN_UP_WHATS_INCLUDED_TRACKING,
  SUBSCRIPTION_TYPES,
  UPGRADE_NOW_TRACKING,
} from '../../constants/subscriptions';
import {
  buildSelfServeLoginUrl,
  buildSelfServeRegisterUrl,
  getHgproBaseurl,
} from '../../utils/service';
import FAQ from '../FAQs/FAQ';
import Button from '../Shared/Button';
import {
  ComparisonTable,
  ContentSection,
  CTASection,
  FeatureCard,
  HealthcareProfessionalCard,
  HeroSection,
  MobileComparisonTable,
  PricingSection,
  SectionHeader,
  VideoSection,
} from './components';
import {
  COMPARISON_TABLE_DATA,
  elevatedContentItems,
  faqData,
  healthcareProfessionalItems,
} from './constants';

const zocDocescriptionJsx = (
  <>
    <p>
      Your Zocdoc scheduling widget can appear automatically on your Elevated
      Profile, providing patients with a seamless and trusted booking
      experience—right where they're searching.
    </p>
    <br />
    <p>
      Not currently on Zocdoc? No problem. You can add your own custom
      scheduling link to your Elevated Profile, making it easy to capture
      appointments directly.
    </p>
    <br />
    <p>
      Enhance your visibility, streamline appointment booking, and make it
      effortless for patients to connect with you—all in one place.
    </p>
  </>
);

const ElevatedLandingPage = () => {
  const baseurl = getHgproBaseurl(window.location.hostname);
  const selfServeLoginUrl = buildSelfServeLoginUrl(baseurl);
  const redirectToSignUp = () => {
    window.location.href = buildSelfServeRegisterUrl(baseurl);
  };
  return (
    <div className="bg-soft-blue font-hg-sans">
      {/* Hero Section */}
      <HeroSection
        backgroundImage="/public/images/elevated-landing-banner.png"
        title="Stand out with an upgraded Healthgrades profile"
        description="Millions of people search Healthgrades every month to connect with healthcare professionals. Ensure your profile or entire practice makes a lasting impression with an Elevated Profile built to help more patients choose you."
        rightImage="/public/images/doctor-illustration.svg"
        rightImageAlt="doctor-illustration"
      />

      {/* Elevated Content Section */}
      <ContentSection>
        <SectionHeader
          title="Elevate your online presence"
          description="Upgrade your profile or practice to an Elevated Profile that puts your expertise front and center with an ad-free, engaging design and easier conversion for patients actively seeking care."
          className="mb-10"
          centered
        />
        <div className="flex flex-col md:flex-row gap-8 md:gap-15 mb-12 md:mb-10">
          {elevatedContentItems.map((item, index) => (
            <FeatureCard
              key={`elevated-content - ${item.title || index} `}
              icon={item.imageUrl}
              title={item.heading}
              description={item.description}
            />
          ))}
        </div>
        <PricingSection
          title={
            <>
              <span className="text-deep-blue-70">Elevated plans</span>
              {` are ${SUBSCRIPTION_TYPES.MONTHLY.displayPrice} per month or ${SUBSCRIPTION_TYPES.ANNUAL.displayPrice} annually`}
            </>
          }
          description={`Elevated Profile Subscriptions are available for individual healthcare professionals or group practices with up to ${MAX_PROVIDERS_LIMIT} providers.`}
          buttonText="Sign Up"
          onClick={redirectToSignUp}
          trackingConfig={SIGN_UP_ELEVATE_PRESENCE_TRACKING}
        />
      </ContentSection>

      {/* Subscription Comparison Table. */}
      <section className="relative flex px-5 md:px-28 py-10 flex-col justify-center items-center gap-10">
        <div className="absolute top-10 md:top-6 right-1 md:right-19 opacity-50 w-1/3 md:w-2/5 h-auto">
          <img alt="heart-logo" src="/public/images/heart-logo-part1.svg" />
        </div>
        <div className="absolute top-0 left-0 md:left-17 right-0 md:right-47 opacity-50 h-auto">
          <img alt="heart-logo" src="/public/images/heart-logo-part2.svg" />
        </div>
        <div className="flex flex-col md:flex-row items-center gap-14 z-10">
          <div className="flex flex-col gap-6px">
            <SectionHeader
              title="What's included in an Elevated Profile Subscription"
              description="Put your expertise front and center — with an ad-free, engaging design and easier conversion for patients actively seeking care."
            />
            <ElementClickTracker {...SIGN_UP_WHATS_INCLUDED_TRACKING}>
              <Button
                variant="primary"
                size="medium"
                className="w-full md:w-50 my-10"
                onClick={redirectToSignUp}
              >
                Sign Up
              </Button>
            </ElementClickTracker>
          </div>
          <div className="hidden md:flex">
            <ComparisonTable features={COMPARISON_TABLE_DATA} />
          </div>
        </div>
        <MobileComparisonTable features={COMPARISON_TABLE_DATA} />
        <div className="px-0 md:px-25 text-center text-gray-90 font-hg-sans text-base font-normal leading-6 z-10">
          Your subscription will auto-renew until you cancel. If you choose to
          cancel your Elevated Profile Subscription, your subscription will end
          on your next billing date, and your profile will revert to the
          standard version.
        </div>
      </section>

      {/* Group Practices Section 1 */}
      <CTASection
        title="Enhance your group practice presence on Healthgrades"
        description={
          <>
            For group practices with up to {MAX_PROVIDERS_LIMIT} providers,
            Healthgrades Elevated Profiles can simplify managing multiple
            physician profiles in one place. This centralized approach ensures
            consistent branding and up-to-date information, enhancing your
            practice's online presence and attracting more patients with an
            ad-free, cohesive experience.
            <br />
            <br />
            Want to upgrade more than {MAX_PROVIDERS_LIMIT} providers?{' '}
            <a
              href="mailto:elevatedprofile@healthgrades.com"
              className="font-semibold text-base leading-6 underline"
            >
              Get in touch
            </a>
          </>
        }
        buttonText="Get Started"
        buttonVariant="secondary"
        imageSrc="/public/images/doctor-experience.png"
        imageAlt="group-practices"
        backgroundImage="/public/images/blue-gradient-background.png"
        overlayImage="/public/images/parallel-lines.svg"
        onClick={redirectToSignUp}
        trackingConfig={GET_STARTED_TRACKING}
      />

      {/* Healthcare Professionals Section */}
      <section className="flex px-6 py-12 md:px-28 md:py-16 flex-col justify-center items-center gap-10 z-20 bg-white relative">
        <SectionHeader
          title="Why healthcare professionals love Healthgrades"
          className="h-20"
          titleClassName="text-center"
          centered
        />
        <div className="flex flex-col md:flex-row items-start gap-8 self-stretch">
          {healthcareProfessionalItems.map((item, index) => (
            <HealthcareProfessionalCard
              key={`healthcare - professional - ${item.title || index} `}
              icon={item.imageUrl}
              title={item.heading}
              description={item.description}
              index={index}
            />
          ))}
        </div>
        <ElementClickTracker {...ELEVATE_PROFILE_TRACKING}>
          <Button
            variant="primary"
            size="medium"
            className="w-full md:w-50"
            onClick={redirectToSignUp}
          >
            Elevate Your Profile
          </Button>
        </ElementClickTracker>
      </section>

      {/* Zocdoc scheduling section */}
      <CTASection
        title="Simplify Scheduling. Maximize Visibility."
        description={zocDocescriptionJsx}
        buttonText="Upgrade My Profile"
        buttonVariant="secondary"
        imageSrc="/public/images/zocdoc-scheduling-widget.svg"
        imageAlt="Zocdoc scheduling widget on Elevated Profile"
        imageClassName="w-full max-w-[386px] md:w-96 md:h-[490px] object-contain flex-shrink-0 md:ml-[155px]"
        backgroundStyle={{
          background:
            'linear-gradient(278deg, rgba(41, 90, 251, 0.90) 30.36%, rgba(2, 2, 234, 0.80) 86.74%), #FFF',
        }}
        loginUrl={selfServeLoginUrl}
        onClick={redirectToSignUp}
        trackingConfig={ELEVATE_PROFILE_TRACKING}
        className="md:pt-14"
        descriptionClassName="space-y-2"
      />

      {/* Ad-Free Experience Section */}
      <VideoSection
        title="No ads. No competitors. No distractions."
        description="With a Healthgrades Elevated Profile, your listing is free from competitor comparisons and consumer ads—ensuring patients stay focused on you, not local alternatives or distractions."
        videoSrc="https://ucmscdn.healthgrades.com/3b/d9/755e076f4fa8aeafcbe5d4dbe54b/elevated-profile-video-v4.mp4"
        videoAlt="ad-free-experience-video"
        buttonText="Upgrade Now"
        onClick={redirectToSignUp}
        buttonClassName="w-full md:w-46"
        trackingConfig={UPGRADE_NOW_TRACKING}
      />

      {/* Group Practices Section 2 */}
      <CTASection
        title="Upgrade to an Elevated Profile today and connect with more patients"
        description={`Elevated plans are ${SUBSCRIPTION_TYPES.MONTHLY.displayPrice} per month or ${SUBSCRIPTION_TYPES.ANNUAL.displayPrice} annually.`}
        buttonText="Elevate Your Profile"
        buttonVariant="secondary"
        imageSrc="/public/images/elevated-doctor-experience.png"
        imageAlt="elevated-doctor-experience"
        backgroundImage="/public/images/blue-gradient-background.png"
        overlayImage="/public/images/parallel-lines.svg"
        className="flex-col md:flex-row justify-between"
        onClick={redirectToSignUp}
        trackingConfig={ELEVATE_PROFILE_TRACKING}
      />

      {/* FAQ Section */}
      <section className="flex justify-center md:items-center px-1 py-5 md:px-50 md:py-15 relative z-20">
        <FAQ data={faqData} />
      </section>
    </div>
  );
};

export default ElevatedLandingPage;
